import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { reportTemplateService } from "../services/report-template.service";
import { CreateReportTemplateDto, UpdateReportTemplateDto } from "../types/dtos/report";
import { useOrganization } from "../../contexts/OrganizationContext";

const TEMPLATES_KEY = "report-templates";

export const useReportTemplates = () => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [TEMPLATES_KEY, selectedOrganization?.id],
    queryFn: () => reportTemplateService.findAll().then((res) => res.data),
    enabled: !!selectedOrganization?.id,
    staleTime: 30000,
  });
};

export const useReportTemplate = (id: string) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [TEMPLATES_KEY, selectedOrganization?.id, id],
    queryFn: () => reportTemplateService.findById(id).then((res) => res.data),
    enabled: !!id && !!selectedOrganization?.id,
  });
};

export const useCreateReportTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReportTemplateDto) =>
      reportTemplateService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
};

export const useUpdateReportTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReportTemplateDto }) =>
      reportTemplateService.update(id, data).then((res) => res.data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, id] });
    },
  });
};

export const useDeleteReportTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reportTemplateService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
};

export const useReportTemplateContent = () => {
  return useMutation({
    mutationFn: (id: string) =>
      reportTemplateService.getContent(id).then((res) => res.data as string),
  });
};
